import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Plus, X, Edit2, Trash2, ChevronUp, ChevronDown, ListChecks } from 'lucide-react';
import { CropSchedule, Practice } from '../types';
import { Language } from '../translations';

const MONTHS = [
  { en: 'January', te: 'జనవరి', hi: 'जनवरी' },
  { en: 'February', te: 'ఫిబ్రవరి', hi: 'फरवरी' },
  { en: 'March', te: 'మార్చి', hi: 'मार्च' },
  { en: 'April', te: 'ఏప్రిల్', hi: 'अप्रैल' },
  { en: 'May', te: 'మే', hi: 'मई' },
  { en: 'June', te: 'జూన్', hi: 'जून' },
  { en: 'July', te: 'జూలై', hi: 'जुलाई' },
  { en: 'August', te: 'ఆగస్టు', hi: 'अगस्त' },
  { en: 'September', te: 'సెప్టెంబర్', hi: 'सितंबर' },
  { en: 'October', te: 'అక్టోబర్', hi: 'अक्टूबर' },
  { en: 'November', te: 'నవంబర్', hi: 'नवंबर' },
  { en: 'December', te: 'డిసెంబర్', hi: 'दिसंबर' }
];

export default function CropCalendarAdmin({ language, t }: { language: Language, t: any }) {
  const [crops, setCrops] = useState<CropSchedule[]>([]);
  const [editingCrop, setEditingCrop] = useState<Partial<CropSchedule> | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showPractices, setShowPractices] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'cropCalendar'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const cropsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CropSchedule[];
      setCrops(cropsData);
    });
    return () => unsubscribe();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCrop) return;

    try {
      const data = {
        ...editingCrop,
        order: editingCrop.order ?? crops.length
      };

      if (editingCrop.id) {
        const { id, ...rest } = data;
        await updateDoc(doc(db, 'cropCalendar', id), rest);
      } else {
        await addDoc(collection(db, 'cropCalendar'), data);
      }
      setEditingCrop(null);
      setIsAdding(false);
      setShowPractices(false);
    } catch (error) {
      console.error("Error saving crop entry:", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t.deleteConfirm)) {
      try {
        await deleteDoc(doc(db, 'cropCalendar', id));
      } catch (error) {
        console.error("Error deleting crop entry:", error);
      }
    }
  };

  const toggleMonth = (type: 'planting' | 'harvest', monthIndex: number) => {
    if (!editingCrop) return;
    const key = type === 'planting' ? 'plantingMonths' : 'harvestMonths';
    const current = (editingCrop[key] as number[]) || [];
    const updated = current.includes(monthIndex)
      ? current.filter(m => m !== monthIndex)
      : [...current, monthIndex];
    setEditingCrop({ ...editingCrop, [key]: updated });
  };

  const addPractice = () => {
    if (!editingCrop) return;
    const newPractice: Practice = {
      id: Math.random().toString(36).substr(2, 9),
      dayOffset: 0,
      title_en: '', title_te: '', title_hi: '',
      details_en: '', details_te: '', details_hi: ''
    };
    setEditingCrop({
      ...editingCrop,
      practices: [...(editingCrop.practices || []), newPractice]
    });
  };

  const removePractice = (id: string) => {
    if (!editingCrop) return;
    setEditingCrop({
      ...editingCrop,
      practices: (editingCrop.practices || []).filter(p => p.id !== id)
    });
  };

  const updatePractice = (id: string, updates: Partial<Practice>) => {
    if (!editingCrop) return;
    setEditingCrop({
      ...editingCrop,
      practices: (editingCrop.practices || []).map(p => p.id === id ? { ...p, ...updates } : p)
    });
  };

  const moveCrop = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= crops.length) return;

    const newCrops = [...crops];
    const temp = newCrops[index].order;
    newCrops[index].order = newCrops[newIndex].order;
    newCrops[newIndex].order = temp;

    try {
      await updateDoc(doc(db, 'cropCalendar', newCrops[index].id), { order: newCrops[index].order });
      await updateDoc(doc(db, 'cropCalendar', newCrops[newIndex].id), { order: newCrops[newIndex].order });
    } catch (error) {
      console.error("Error moving crop:", error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-[#1b7d36] font-bold">{t.cropCalendar}</h3>
        {!isAdding && !editingCrop && (
          <button 
            onClick={() => {
              setEditingCrop({
                name_en: '', name_te: '', name_hi: '',
                plantingMonths: [], harvestMonths: [],
                details_en: '', details_te: '', details_hi: '',
                practices: [],
                order: crops.length
              });
              setIsAdding(true);
            }}
            className="bg-[#1b7d36] text-white p-2 rounded-xl shadow-md"
          >
            <Plus size={20} />
          </button>
        )}
      </div>

      {(isAdding || editingCrop?.id) && editingCrop && (
        <form onSubmit={handleSave} className="bg-white p-6 rounded-[32px] border border-stone-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-bold text-stone-800">{editingCrop.id ? t.editCropEntry : t.addNewCropEntry}</h4>
            <button type="button" onClick={() => { setEditingCrop(null); setIsAdding(false); setShowPractices(false); }} className="text-stone-400">
              <X size={20} />
            </button>
          </div>

          <div className="flex gap-4 mb-4 border-b border-stone-100">
            <button 
              type="button"
              onClick={() => setShowPractices(false)}
              className={`pb-2 px-1 text-sm font-bold transition-all ${!showPractices ? 'text-[#1b7d36] border-b-2 border-[#1b7d36]' : 'text-stone-400'}`}
            >
              {t.basicInfo || 'Basic Info'}
            </button>
            <button 
              type="button"
              onClick={() => setShowPractices(true)}
              className={`pb-2 px-1 text-sm font-bold transition-all ${showPractices ? 'text-[#1b7d36] border-b-2 border-[#1b7d36]' : 'text-stone-400'}`}
            >
              {t.practices} ({(editingCrop.practices || []).length})
            </button>
          </div>

          {!showPractices ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-1">{t.cropNameEn}</label>
              <input 
                type="text" 
                required
                value={editingCrop.name_en || ''}
                onChange={e => setEditingCrop({ ...editingCrop, name_en: e.target.value })}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-xl px-4 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-1">{t.cropNameTe}</label>
              <input 
                type="text" 
                value={editingCrop.name_te || ''}
                onChange={e => setEditingCrop({ ...editingCrop, name_te: e.target.value })}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-xl px-4 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-1">{t.cropNameHi}</label>
              <input 
                type="text" 
                value={editingCrop.name_hi || ''}
                onChange={e => setEditingCrop({ ...editingCrop, name_hi: e.target.value })}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-xl px-4 py-2 text-sm"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-2">{t.plantingMonths}</label>
              <div className="flex flex-wrap gap-2">
                {MONTHS.map((month, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleMonth('planting', i)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      (editingCrop.plantingMonths || []).includes(i)
                        ? 'bg-emerald-500 text-white'
                        : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                    }`}
                  >
                    {month[language].substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-stone-500 block mb-2">{t.harvestMonths}</label>
              <div className="flex flex-wrap gap-2">
                {MONTHS.map((month, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleMonth('harvest', i)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      (editingCrop.harvestMonths || []).includes(i)
                        ? 'bg-amber-500 text-white'
                        : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                    }`}
                  >
                    {month[language].substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-1">{t.detailsEn}</label>
              <textarea 
                rows={2}
                value={editingCrop.details_en || ''}
                onChange={e => setEditingCrop({ ...editingCrop, details_en: e.target.value })}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-xl px-4 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-1">{t.detailsTe}</label>
              <textarea 
                rows={2}
                value={editingCrop.details_te || ''}
                onChange={e => setEditingCrop({ ...editingCrop, details_te: e.target.value })}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-xl px-4 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-stone-500 block mb-1">{t.detailsHi}</label>
              <textarea 
                rows={2}
                value={editingCrop.details_hi || ''}
                onChange={e => setEditingCrop({ ...editingCrop, details_hi: e.target.value })}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-xl px-4 py-2 text-sm"
              />
            </div>
          </div>
          </div>
          ) : (
            <div className="space-y-6">
              {(editingCrop.practices || []).map((practice) => (
                <div key={practice.id} className="p-4 bg-stone-50 rounded-2xl border border-stone-200 space-y-3 relative">
                  <button 
                    type="button" 
                    onClick={() => removePractice(practice.id)}
                    className="absolute -top-2 -right-2 bg-rose-500 text-white p-1 rounded-full shadow-md"
                  >
                    <X size={14} />
                  </button>
                  
                  <div>
                    <label className="text-xs font-bold text-stone-500 block mb-1">{t.dayOffset}</label>
                    <input 
                      type="number" 
                      value={practice.dayOffset}
                      onChange={e => updatePractice(practice.id, { dayOffset: parseInt(e.target.value) || 0 })}
                      className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-bold text-stone-500 block mb-1">{t.practiceTitleEn}</label>
                      <input 
                        type="text" 
                        value={practice.title_en}
                        onChange={e => updatePractice(practice.id, { title_en: e.target.value })}
                        className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-stone-500 block mb-1">{t.practiceTitleTe}</label>
                      <input 
                        type="text" 
                        value={practice.title_te}
                        onChange={e => updatePractice(practice.id, { title_te: e.target.value })}
                        className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-stone-500 block mb-1">{t.practiceTitleHi}</label>
                      <input 
                        type="text" 
                        value={practice.title_hi}
                        onChange={e => updatePractice(practice.id, { title_hi: e.target.value })}
                        className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-bold text-stone-500 block mb-1">{t.practiceDetailsEn}</label>
                      <textarea 
                        rows={2}
                        value={practice.details_en}
                        onChange={e => updatePractice(practice.id, { details_en: e.target.value })}
                        className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-stone-500 block mb-1">{t.practiceDetailsTe}</label>
                      <textarea 
                        rows={2}
                        value={practice.details_te}
                        onChange={e => updatePractice(practice.id, { details_te: e.target.value })}
                        className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-stone-500 block mb-1">{t.practiceDetailsHi}</label>
                      <textarea 
                        rows={2}
                        value={practice.details_hi}
                        onChange={e => updatePractice(practice.id, { details_hi: e.target.value })}
                        className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button 
                type="button"
                onClick={addPractice}
                className="w-full py-3 border-2 border-dashed border-stone-200 rounded-2xl text-stone-400 font-bold flex items-center justify-center gap-2 hover:border-[#1b7d36] hover:text-[#1b7d36] transition-all"
              >
                <Plus size={20} />
                {t.addPractice}
              </button>
            </div>
          )}

          <button 
            type="submit"
            className="w-full bg-[#1b7d36] text-white py-3 rounded-2xl font-bold shadow-md"
          >
            {t.save}
          </button>
        </form>
      )}

      <div className="space-y-3">
        {crops.map((crop, index) => (
          <div key={crop.id} className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1 mr-2">
                <button 
                  onClick={() => moveCrop(index, 'up')}
                  disabled={index === 0}
                  className="text-stone-300 disabled:opacity-20 hover:text-[#1b7d36]"
                >
                  <ChevronUp size={16} />
                </button>
                <button 
                  onClick={() => moveCrop(index, 'down')}
                  disabled={index === crops.length - 1}
                  className="text-stone-300 disabled:opacity-20 hover:text-[#1b7d36]"
                >
                  <ChevronDown size={16} />
                </button>
              </div>
              <div>
                <h4 className="font-bold text-stone-700">
                  {(crop[`name_${language}` as keyof CropSchedule] as string)?.trim() || crop.name_en}
                </h4>
                <p className="text-[10px] text-stone-400 uppercase tracking-widest flex items-center gap-2">
                  <span>Planting: {crop.plantingMonths?.length || 0}m • Harvest: {crop.harvestMonths?.length || 0}m</span>
                  <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                    <ListChecks size={10} />
                    {crop.practices?.length || 0}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <button onClick={() => setEditingCrop(crop)} className="text-blue-500 hover:text-blue-600 transition-colors">
                <Edit2 size={18} />
              </button>
              <button onClick={() => handleDelete(crop.id)} className="text-rose-500 hover:text-rose-600 transition-colors">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
        {crops.length === 0 && !isAdding && (
          <p className="text-center text-stone-400 text-xs py-10 italic">No crop entries yet.</p>
        )}
      </div>
    </div>
  );
}
